/**
 * Confirmation modal shown before starting time tracking on a pending quote.
 * Large touch-friendly buttons for iPad/mobile use.
 */
export default function PendingQuoteConfirmModal({
  quote,
  operation,
  onConfirm,
  onCancel,
  isLoading = false,
}) {
  const projectName = quote?.user_Text4 || `Quote #${quote?.quoteNumber}`;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: "#1a1a1a",
          borderRadius: 16,
          padding: 28,
          width: "100%",
          maxWidth: 480,
          border: "1px solid #f59e0b",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "rgba(245, 158, 11, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: 32, height: 32, color: "#f59e0b" }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, color: "#fff", fontWeight: 600 }}>
            Start Work on Pending Quote?
          </h2>
        </div>

        {/* Project Info */}
        <div
          style={{
            backgroundColor: "#111",
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#71717a", marginBottom: 4 }}>
              PROJECT
            </div>
            <div style={{ fontSize: 18, color: "#fff", fontWeight: 500 }}>
              {projectName}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: "#71717a", marginBottom: 4 }}>
                QUOTE #
              </div>
              <div style={{ fontSize: 16, color: "#fbbf24", fontWeight: 600 }}>
                {quote?.quoteNumber || "—"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#71717a", marginBottom: 4 }}>
                OPERATION
              </div>
              <div style={{ fontSize: 16, color: "#60a5fa", fontWeight: 500 }}>
                {operation?.operationCode || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div
          style={{
            padding: 12,
            backgroundColor: "rgba(245, 158, 11, 0.1)",
            border: "1px solid rgba(245, 158, 11, 0.3)",
            borderRadius: 8,
            marginBottom: 24,
            fontSize: 13,
            color: "#fcd34d",
            lineHeight: 1.5,
            textAlign: "center",
          }}
        >
          This quote doesn't have orders yet. Your time will be tracked and can be assigned to a job later.
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: "20px 24px",
              fontSize: 18,
              fontWeight: 700,
              backgroundColor: isLoading ? "#065f46" : "#10b981",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              cursor: isLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              minHeight: 64,
              transition: "background-color 0.2s",
            }}
          >
            {isLoading ? (
              <>
                <svg
                  style={{ width: 24, height: 24, animation: "spin 1s linear infinite" }}
                  viewBox="0 0 24 24"
                >
                  <circle
                    style={{ opacity: 0.25 }}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    style={{ opacity: 0.75 }}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Starting...
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 28, height: 28 }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                CONFIRM - Start Timer
              </>
            )}
          </button>

          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: "16px 24px",
              fontSize: 16,
              backgroundColor: "transparent",
              color: "#a1a1aa",
              border: "1px solid #3f3f46",
              borderRadius: 12,
              cursor: isLoading ? "not-allowed" : "pointer",
              minHeight: 56,
            }}
          >
            Cancel
          </button>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
